import User16 from '@carbon/icons-react/es/user/16';
// eslint-disable-next-line no-restricted-imports
import { Avatar as AntAvatar, Badge as AntBadge, Image } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { UserStatus } from 'api/models';
import DeactivatedUser from 'assets/svgs/deactivated_user.svg';
import { Caption01 } from 'components/styles';
import { initials } from 'utils/initials';
import { AvatarProps } from './types';

const ImageStyles = css`
  .ant-image {
    display: inherit;
    position: inherit;
    height: 100%;

    img {
      object-fit: cover;
      height: 100%;
    }
  }
`;

const CompanyAvatar = styled(AntAvatar)`
  border: solid 1px var(--white-100);
  bottom: -30%;
  height: 12px;
  right: 2px;
  top: auto;
  width: 12px;
  z-index: 1;

  ${ImageStyles};
`;

const Badge = styled(AntBadge)`
  ${CompanyAvatar}.ant-avatar {
    &-sm {
      height: 10px;
      width: 10px;
    }

    &-lg {
      height: 14px;
      width: 14px;
    }
  }
`;

const StyledAvatar = styled(AntAvatar)`
  align-items: center;
  display: inline-flex;
  justify-content: center;
  flex-shrink: 0;

  &.ant-avatar-image,
  &.ant-avatar-icon {
    background: var(--coolest-gray-20);
    flex-shrink: 0;
  }

  .ant-avatar-string {
    ${Caption01};
    color: var(--white-100);
  }
  ${ImageStyles};
`;

const AvatarColors = [
  'red',
  'magenta',
  'purple',
  'blue',
  'cyan',
  'teal',
  'green',
  'orange',
  'yellow',
];

/**
 * @method getColor
 * @private
 * @param str
 * @description generates color schemes based on the string provided. It is used on #1
 * @returns
 */
const getColor = (str = '') => {
  if (!str.length) {
    return 'var(--coolest-gray-20)';
  }

  const charcodeOfStr = initials(str).toLowerCase().charCodeAt(0);

  const colorIndex = Math.abs(charcodeOfStr - 'a'.charCodeAt(0));

  return `var(--${AvatarColors[colorIndex % 9]}-gradient)`;
};

/**
 * @description Used to get the avatar color as a 50% variant. Similar functionality to getColor function
 * @param str
 * @returns
 */
export const getColorVariantAsString = (str = '') => {
  if (!str.length) {
    return 'var(--coolest-gray-20)';
  }

  const charcodeOfStr = initials(str).toLowerCase().charCodeAt(0);

  const colorIndex = charcodeOfStr - 'a'.charCodeAt(0);

  return `var(--${AvatarColors[colorIndex % 9]}-50)`;
};

/**
 * @description Custom wrapper for antd avatar component.
 * #1 Can be used to generate text based avatar if no image is provided
 * #2 Adds Alt to the Image provided from name
 */
export const Avatar: FC<AvatarProps> = ({
  className,
  name = '',
  src,
  companyLogoUrl,
  shape = 'circle',
  size = 'default',
  icon = <User16 />,
  isPlaceholder,
  userStatus,
}) => {
  const hasEitherNameOrSrc = src != null || name.length > 0;
  const [hasErrored, setErrored] = useState(false);

  const [hasCompanyErrored, setCompanyErrored] = useState(false);

  const validSrc = useMemo(
    () => src != null && src !== '' && !hasErrored,
    [src, hasErrored],
  );

  useEffect(() => {
    setErrored(false);
  }, [src]);

  useEffect(() => {
    setCompanyErrored(false);
  }, [companyLogoUrl]);

  return (
    <>
      {companyLogoUrl != null && companyLogoUrl !== '' ? (
        <Badge
          data-cy={`avatar-badge-${name}`}
          count={
            !hasCompanyErrored ? (
              <CompanyAvatar
                src={
                  <Image
                    src={`${companyLogoUrl}?v2`}
                    crossOrigin="anonymous"
                    alt="company-logo"
                    preview={false}
                    onError={() => setCompanyErrored(true)}
                  />
                }
                size={size}
                shape={shape}
                data-email-template-ignore
              />
            ) : null
          }
        >
          <StyledAvatar
            data-rocketlane-avatar
            className={className}
            icon={!hasEitherNameOrSrc && icon}
            shape={shape}
            size={size}
            src={
              userStatus === UserStatus.Inactive ? (
                DeactivatedUser
              ) : validSrc ? (
                <Image
                  alt={name}
                  src={`${src}?v2`}
                  crossOrigin="anonymous"
                  preview={false}
                  onError={() => setErrored(true)}
                />
              ) : null
            }
            style={
              userStatus === UserStatus.Inactive
                ? undefined
                : {
                    background: validSrc || isPlaceholder ? '' : getColor(name),
                  }
            }
          >
            {initials(name)}
          </StyledAvatar>
        </Badge>
      ) : (
        <StyledAvatar
          data-rocketlane-avatar
          className={className}
          icon={!hasEitherNameOrSrc && icon}
          shape={shape}
          size={size}
          data-cy={`avatar-badge-${name}`}
          src={
            userStatus === UserStatus.Inactive ? (
              DeactivatedUser
            ) : validSrc ? (
              <Image
                alt={name}
                src={`${src}?v2`}
                crossOrigin="anonymous"
                preview={false}
                onError={() => setErrored(true)}
              />
            ) : null
          }
          style={
            userStatus === UserStatus.Inactive
              ? undefined
              : {
                  background: validSrc || isPlaceholder ? '' : getColor(name),
                }
          }
        >
          {initials(name)}
        </StyledAvatar>
      )}
    </>
  );
};
